import { useCallback, useEffect } from 'react';

/**
 * PDFのキーボードナビゲーションを管理するカスタムフック
 *
 * @param scrollContainerRef - スクロールコンテナへの参照
 * @param page - 現在のページ番号
 * @param pageTotal - 総ページ数
 * @param handlePageChange - ページ変更ハンドラー
 */
export const useKeyboardNavigation = (
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  page: number,
  pageTotal: number,
  handlePageChange: (page: number) => void
) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (!scrollContainerRef.current?.contains(document.activeElement)) {
        return;
      }

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          scrollContainerRef.current?.scrollBy({
            top: -80,
            behavior: 'smooth',
          });
          break;
        case 'ArrowDown':
          event.preventDefault();
          scrollContainerRef.current?.scrollBy({
            top: 80,
            behavior: 'smooth',
          });
          break;
        case 'PageUp':
          event.preventDefault();
          if (page > 1) {
            handlePageChange(page - 1);
          }
          break;
        case 'PageDown':
          event.preventDefault();
          if (page < pageTotal) {
            handlePageChange(page + 1);
          }
          break;
        case 'Home':
          event.preventDefault();
          scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          break;
        case 'End':
          event.preventDefault();
          scrollContainerRef.current?.scrollTo({
            top: scrollContainerRef.current.scrollHeight,
            behavior: 'smooth',
          });
          break;
      }
    },
    [scrollContainerRef, page, pageTotal, handlePageChange]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
};
